import React, { useState, useEffect, Suspense, lazy } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Dimmer,
  Loader,
  Card,
  Pagination,
  Segment,
  Icon,
  Button,
  Header,
  Checkbox,
  Message,
  Divider,
  Label,
  Grid,
  Modal,
} from "semantic-ui-react";

import {
  LOAD_EMPLOYEES,
  UPDATE_EMPLOYEE_PAGE_NUMBER,
  UPDATE_EMPLOYEE_QUERY_PARAMS,
  SG_BULK_ADD_ORG_GROUP_MEMBERS,
  SG_FETCH_ORG_GROUP,
} from "constants/actions";

const SearchBar = lazy(() => import("../employees/SearchBar"));

const AddMembers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: groupId } = useParams();

  const [organizationId, setOrganizationId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [selectAllOnPage, setSelectAllOnPage] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [operationType, setOperationType] = useState(null); // 'selected', 'all', 'all-org'

  const { get_employees, get_selectedOrg, bulkAddResult, loading, orgGroupDetail } = useSelector(
    (state) => ({
      get_employees: state.employees,
      get_selectedOrg: state.selectedOrg,
      bulkAddResult: state.groups.bulkAddResult,
      loading: state.groups.loading,
      orgGroupDetail: state.groups.orgGroupDetail,
    }),
    shallowEqual
  );

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch group details
  useEffect(() => {
    if (groupId) {
      dispatch({ type: SG_FETCH_ORG_GROUP, payload: { id: groupId } });
    }
  }, [dispatch, groupId]);

  // Set organization from group details
  useEffect(() => {
    if (orgGroupDetail && orgGroupDetail.organization) {
      setOrganizationId(orgGroupDetail.organization);
      // Immediately apply filters when organization is set
      applyFilters(1, orgGroupDetail.organization, searchTerm);
    }
  }, [orgGroupDetail]);

  // Apply filters
  const applyFilters = (pageNo = 1, orgId = organizationId, search = searchTerm) => {
    let params = [];
    if (pageNo) params.push(`page=${pageNo}`);
    if (orgId) params.push(`organization=${orgId}`);
    if (search) params.push(`search=${search}`);

    dispatch({ type: UPDATE_EMPLOYEE_QUERY_PARAMS, payload: params });
  };

  useEffect(() => {
    // Load employees based on query params
    if (get_employees?.queryParams?.length > 0) {
      dispatch({ type: LOAD_EMPLOYEES, payload: get_employees?.queryParams.join("&") });
    } else {
      dispatch({ type: LOAD_EMPLOYEES });
    }
  }, [dispatch, get_employees.queryParams]);

  useEffect(() => {
    // Update pagination
    if (get_employees) {
      setPage(get_employees.pageNumber);
      setTotalPages(Math.ceil(get_employees.employees.count / get_employees.employees.page_size));
    }
  }, [get_employees]);

  useEffect(() => {
    // Reset page selection when page changes
    setSelectAllOnPage(false);
  }, [page]);

  const handlePageChange = (event, { activePage }) => {
    applyFilters(activePage, organizationId, searchTerm);
    setPage(activePage);
    dispatch({ type: UPDATE_EMPLOYEE_PAGE_NUMBER, payload: activePage });
  };

  const handleEmployeeToggle = (employeeId) => {
    const newSelected = new Set(selectedEmployees);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      newSelected.add(employeeId);
    }
    setSelectedEmployees(newSelected);
  };

  const handleSelectAllOnPage = () => {
    if (selectAllOnPage) {
      // Deselect all on page
      const newSelected = new Set(selectedEmployees);
      get_employees?.employees?.results?.forEach((emp) => {
        newSelected.delete(emp.id);
      });
      setSelectedEmployees(newSelected);
      setSelectAllOnPage(false);
    } else {
      // Select all on page
      const newSelected = new Set(selectedEmployees);
      get_employees?.employees?.results?.forEach((emp) => {
        newSelected.add(emp.id);
      });
      setSelectedEmployees(newSelected);
      setSelectAllOnPage(true);
    }
  };

  const handleAddSelected = () => {
    if (selectedEmployees.size === 0) {
      alert("Please select at least one employee");
      return;
    }
    setOperationType("selected");
    setShowConfirmModal(true);
  };

  const handleAddAllFromOrg = (replace = false) => {
    if (!organizationId) {
      alert("Please select an organization first");
      return;
    }
    setOperationType(replace ? "all-org-replace" : "all-org");
    setShowConfirmModal(true);
  };

  const confirmAddMembers = () => {
    let payload = { id: groupId };

    if (operationType === "selected") {
      payload.employee_ids = Array.from(selectedEmployees);
    } else if (operationType === "all-org") {
      payload.add_all_from_org = true;
    } else if (operationType === "all-org-replace") {
      payload.add_all_from_org = true;
      payload.replace = true;
    }

    dispatch({
      type: SG_BULK_ADD_ORG_GROUP_MEMBERS,
      payload,
    });

    setShowConfirmModal(false);
  };

  const renderConfirmModal = () => {
    let title = "";
    let content = "";

    if (operationType === "selected") {
      title = "Add Selected Employees";
      content = `You are about to add ${selectedEmployees.size} selected employee(s) to the group.`;
    } else if (operationType === "all-org") {
      title = "Add All From Organization";
      content = `You are about to add ALL active employees from the organization to the group. This will NOT remove existing members.`;
    } else if (operationType === "all-org-replace") {
      title = "Replace With All From Organization";
      content = `You are about to REPLACE all current members with ALL active employees from the organization. Existing members will be removed.`;
    }

    return (
      <Modal open={showConfirmModal} onClose={() => setShowConfirmModal(false)} size="small">
        <Modal.Header>{title}</Modal.Header>
        <Modal.Content>
          <p>{content}</p>
          {orgGroupDetail && (
            <Message info>
              <strong>Group:</strong> {orgGroupDetail.name || orgGroupDetail.id}
              <br />
              <strong>Current Members:</strong> {orgGroupDetail.member_count || 0}
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button
            positive
            icon="checkmark"
            labelPosition="right"
            content="Confirm"
            onClick={confirmAddMembers}
            loading={loading}
          />
        </Modal.Actions>
      </Modal>
    );
  };

  const renderBulkAddResult = () => {
    if (!bulkAddResult || bulkAddResult.error) {
      if (bulkAddResult?.error) {
        return (
          <Message negative icon>
            <Icon name="times circle" />
            <Message.Content>
              <Message.Header>Operation Failed</Message.Header>
              {bulkAddResult.error.error || bulkAddResult.error.message || "An error occurred"}
            </Message.Content>
          </Message>
        );
      }
      return null;
    }

    const hasSkipped = bulkAddResult.skipped_employees && bulkAddResult.skipped_employees.length > 0;

    return (
      <Message success={!hasSkipped} warning={hasSkipped} icon>
        <Icon name={hasSkipped ? "warning sign" : "check circle"} />
        <Message.Content>
          <Message.Header>Members Added Successfully - {bulkAddResult.group_name}</Message.Header>
          <Message.List>
            <Message.Item>
              <Label color="green" size="small">
                Added: {bulkAddResult.added_count}
              </Label>
            </Message.Item>
            <Message.Item>
              <strong>Total Members Now:</strong> {bulkAddResult.total_members}
            </Message.Item>
            {bulkAddResult.replaced && (
              <Message.Item>
                <Label color="orange" size="small">
                  Replaced existing members
                </Label>
              </Message.Item>
            )}
            {bulkAddResult.add_all_from_org && (
              <Message.Item>
                <Label color="blue" size="small">
                  Added all from: {bulkAddResult.organization_name}
                </Label>
              </Message.Item>
            )}
          </Message.List>

          {hasSkipped && (
            <Segment basic>
              <Header as="h5" color="orange">
                Skipped Employees:
              </Header>
              <Message warning size="small">
                {bulkAddResult.skipped_employees.map((skip, idx) => (
                  <div key={idx}>
                    <strong>ID {skip.employee_id}:</strong> {skip.reason}
                  </div>
                ))}
              </Message>
            </Segment>
          )}

          <Button
            primary
            style={{ marginTop: "10px" }}
            onClick={() => navigate(`/app/groups/organization/${groupId}/employees`)}
          >
            View Group Members
          </Button>
        </Message.Content>
      </Message>
    );
  };

  return (
    <Container>
      <Header as="h2" icon>
        <Icon name="user plus" />
        Add Members to Group
        <Header.Subheader>
          {orgGroupDetail && `Group: ${orgGroupDetail.name || orgGroupDetail.id}`}
        </Header.Subheader>
      </Header>

      <Divider />

      {/* Bulk Add Result */}
      {bulkAddResult && renderBulkAddResult()}

      {/* Action Bar */}
      <ActionBar>
        <Grid columns={2} stackable>
          <Grid.Column>
            <Header as="h4">
              Selected: {selectedEmployees.size}
              {selectedEmployees.size > 0 && (
                <Label color="blue" style={{ marginLeft: "10px" }}>
                  {selectedEmployees.size}
                </Label>
              )}
            </Header>
          </Grid.Column>
          <Grid.Column textAlign="right">
            <Button.Group>
              <Button
                primary
                icon="plus"
                labelPosition="left"
                content={`Add Selected (${selectedEmployees.size})`}
                onClick={handleAddSelected}
                disabled={selectedEmployees.size === 0 || loading}
                title="Add only the employees you've selected with checkboxes"
              />
              <Button
                color="teal"
                icon="users"
                labelPosition="left"
                content="Add All Active"
                onClick={() => handleAddAllFromOrg(false)}
                disabled={!organizationId || loading}
                title="Add ALL active employees from this organization (keeps existing members)"
              />
              <Button
                color="orange"
                icon="refresh"
                labelPosition="left"
                content="Replace All"
                onClick={() => handleAddAllFromOrg(true)}
                disabled={!organizationId || loading}
                title="REPLACE all current members with ALL active employees from this organization"
              />
            </Button.Group>
          </Grid.Column>
        </Grid>
        
        {/* Help Text */}
        <Message size='small' style={{ marginTop: '15px' }}>
          <Message.Header>Quick Guide:</Message.Header>
          <Message.List>
            <Message.Item><strong>Add Selected:</strong> Add only the employees you've checked</Message.Item>
            <Message.Item><strong>Add All Active:</strong> Add ALL active employees from this organization (keeps existing members)</Message.Item>
            <Message.Item><strong>Replace All:</strong> Remove all current members and replace with ALL active employees from this organization</Message.Item>
          </Message.List>
        </Message>
      </ActionBar>

      {/* Organization Info */}
      {orgGroupDetail && (
        <Message info icon>
          <Icon name='building' />
          <Message.Content>
            <Message.Header>Group Organization</Message.Header>
            <p>
              <strong>Organization ID:</strong> {orgGroupDetail.organization}
              {orgGroupDetail.organization_name && (
                <span> - {orgGroupDetail.organization_name}</span>
              )}
            </p>
            <p style={{ fontSize: '0.9em', marginTop: '5px' }}>
              Showing employees from this organization only
            </p>
          </Message.Content>
        </Message>
      )}

      {/* Search and Category Filter */}
      <SectionDiv>
        <Suspense fallback={<Loader active />}>
          <SearchBar
            searchTerm={searchTerm}
            changeSearchTerm={setSearchTerm}
            handlePageChange={handlePageChange}
          />
        </Suspense>
      </SectionDiv>

      {/* Employee List */}
      <Segment raised>
        <Header as="h3">
          <Icon name="users" />
          Available Employees ({get_employees?.employees?.count || 0})
        </Header>

        <SelectAllBar>
          <Checkbox
            label={`Select All on Page (${get_employees?.employees?.results?.length || 0})`}
            checked={selectAllOnPage}
            onChange={handleSelectAllOnPage}
          />
        </SelectAllBar>

        <SectionDiv>
          <Pagination activePage={page} totalPages={totalPages} onPageChange={handlePageChange} />
        </SectionDiv>

        <Dimmer active={loading}>
          <Loader />
        </Dimmer>

        <Card.Group>
          <Suspense fallback={<Loader active />}>
            {get_employees?.employees?.results?.map((employee) => (
              <EmployeeCard key={employee.id}>
                <Card fluid>
                  <Card.Content style={{ paddingLeft: '50px' }}>
                    <CheckboxWrapper>
                      <Checkbox
                        checked={selectedEmployees.has(employee.id)}
                        onChange={() => handleEmployeeToggle(employee.id)}
                      />
                    </CheckboxWrapper>
                    <Card.Header>
                      {employee.first_name} {employee.last_name}
                      <Label size="mini" style={{ marginLeft: "10px" }}>
                        ID: {employee.id}
                      </Label>
                    </Card.Header>
                    <Card.Meta>{employee.email}</Card.Meta>
                    {employee.organization_name && (
                      <Card.Description>
                        <Icon name="building" />
                        {employee.organization_name}
                      </Card.Description>
                    )}
                  </Card.Content>
                </Card>
              </EmployeeCard>
            ))}
          </Suspense>
        </Card.Group>

        <SectionDiv>
          <Pagination activePage={page} totalPages={totalPages} onPageChange={handlePageChange} />
        </SectionDiv>
      </Segment>

      {/* Bottom Action Bar */}
      <ActionBar>
        <Button onClick={() => navigate(-1)}>
          <Icon name="arrow left" />
          Back to Group
        </Button>
        <Button
          primary
          icon="plus"
          labelPosition="left"
          content={`Add Selected (${selectedEmployees.size})`}
          onClick={handleAddSelected}
          disabled={selectedEmployees.size === 0 || loading}
        />
      </ActionBar>

      {/* Confirmation Modal */}
      {renderConfirmModal()}
    </Container>
  );
};

export default AddMembers;

// Styled Components
const Container = styled.div`
  padding: 30px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const SectionDiv = styled.div`
  padding: 15px 0;
  text-align: center;
`;

const ActionBar = styled.div`
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  margin: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const SelectAllBar = styled.div`
  padding: 15px;
  background: #e8f4f8;
  border-radius: 6px;
  margin-bottom: 15px;
`;

const EmployeeCard = styled.div`
  position: relative;
  width: 100%;

  .card {
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transform: translateY(-2px);
    }
  }
`;

const CheckboxWrapper = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 10;
`;
